/*
  Selecteur de variante du bloc d'achat de la page d'accueil.

  Volontairement independant de VariantSelects, qui est couple a la page produit
  (galerie media, retrait en magasin, note de stock) et leverait des erreurs ici.
  Cette classe ne fait que trois choses : resoudre la variante, mettre a jour le
  prix, et renseigner l'input id du formulaire.
*/

class BuyInline extends HTMLElement {
  connectedCallback() {
    this.variantsScript = this.querySelector('[data-buy-variants]');
    this.idInput = this.querySelector('[data-buy-variant-id]');
    this.submit = this.querySelector('[data-buy-submit]');
    this.priceWrap = this.querySelector('[data-buy-price]');
    // Chaque piece a sa propre photo : on la cherche dans le panneau de la
    // piece, pas dans toute la section qui peut en contenir plusieurs.
    const piece = this.closest('[data-buy-piece]') || this.closest('.buy');
    this.image = piece ? piece.querySelector('[data-buy-image]') : null;

    if (!this.variantsScript || !this.idInput) return;

    try {
      this.variants = JSON.parse(this.variantsScript.textContent);
    } catch (error) {
      return;
    }

    this.fieldsets = Array.from(this.querySelectorAll('[data-buy-option-index]'));
    if (!this.fieldsets.length) return;

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-buy-value]')) this.onChange();
    });

    this.onChange();
  }

  selectedOptions() {
    return this.fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('[data-buy-value]:checked');
      return checked ? checked.value : null;
    });
  }

  onChange() {
    const selected = this.selectedOptions();
    const variant = this.variants.find((candidate) =>
      candidate.options.every((value, index) => value === selected[index])
    );

    this.updateAvailability(variant);
    this.updatePrice(variant);
    this.updateImage(variant);
    this.updateUnavailableValues(selected);

    if (variant) this.idInput.value = variant.id;
  }

  updateAvailability(variant) {
    if (!this.submit) return;

    const label = this.submit.querySelector('span');
    const strings = window.variantStrings || {};

    if (!variant) {
      this.submit.setAttribute('disabled', 'disabled');
      this.idInput.setAttribute('disabled', 'disabled');
      if (label && strings.unavailable) label.textContent = strings.unavailable;
      return;
    }

    this.idInput.removeAttribute('disabled');

    if (variant.available) {
      this.submit.removeAttribute('disabled');
      if (label && strings.addToCart) label.textContent = strings.addToCart;
    } else {
      this.submit.setAttribute('disabled', 'disabled');
      if (label && strings.soldOut) label.textContent = strings.soldOut;
    }
  }

  /*
    L'image suit la couleur choisie. On ne touche a rien tant que la variante n'a
    pas d'image propre : mieux vaut garder celle qui est affichee qu'en retirer
    une. On precharge avant de remplacer, pour eviter le trou blanc entre le
    changement de src et l'arrivee du fichier.
  */
  updateImage(variant) {
    if (!this.image || !variant || !variant.image) return;
    if (this.image.getAttribute('src') === variant.image.w1200) return;

    const suivante = new Image();
    suivante.src = variant.image.w1200;

    const poser = () => {
      this.image.src = variant.image.w1200;
      this.image.srcset = [
        variant.image.w600 + ' 600w',
        variant.image.w900 + ' 900w',
        variant.image.w1200 + ' 1200w',
      ].join(', ');
      if (variant.image.alt) this.image.alt = variant.image.alt;
    };

    if (suivante.complete) poser();
    else {
      suivante.addEventListener('load', poser, { once: true });
      suivante.addEventListener('error', poser, { once: true });
    }
  }

  updatePrice(variant) {
    if (!this.priceWrap || !variant) return;

    let markup = '<span class="buy__price-current">' + variant.price + '</span>';
    if (variant.on_sale && variant.compare_at_price) {
      markup += '<s class="buy__price-compare">' + variant.compare_at_price + '</s>';
    }
    this.priceWrap.innerHTML = markup;
    this.priceWrap.classList.toggle('buy__price--on-sale', Boolean(variant.on_sale));
  }

  /*
    Barre les tailles qui ne menent a aucune combinaison disponible, en tenant
    compte des choix deja faits sur les autres options.
  */
  updateUnavailableValues(selected) {
    this.fieldsets.forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('[data-buy-value]').forEach((input) => {
        const probe = selected.slice();
        probe[optionIndex] = input.value;

        const reachable = this.variants.some(
          (candidate) =>
            candidate.available &&
            candidate.options.every((value, index) => index === optionIndex || value === probe[index]) &&
            candidate.options[optionIndex] === input.value
        );

        input.classList.toggle('is-unavailable', !reachable);
        const label = fieldset.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.toggle('is-unavailable', !reachable);
      });
    });
  }
}

if (!customElements.get('buy-inline')) {
  customElements.define('buy-inline', BuyInline);
}

/*
  Onglets du bloc d'achat quand il propose plusieurs pieces.

  Un lien vers #achat-<handle> (cartes de la collection, menu, lien partage)
  ouvre le bon onglet puis fait defiler jusqu'au debut de la section, pour que
  les onglets restent visibles au-dessus de la piece.
*/
class BuyPieces extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-buy-tab]'));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.buyTab, true));
    });

    this.querySelector('[role="tablist"]').addEventListener('keydown', (event) => this.onKeydown(event));

    this.onLinkClick = (event) => {
      const link = event.target.closest('a[href*="#achat-"]');
      if (!link) return;
      const id = link.getAttribute('href').split('#')[1];
      if (!this.hasPanel(id)) return;
      event.preventDefault();
      this.select(id, false);
      this.reveal(id);
    };
    document.addEventListener('click', this.onLinkClick);

    this.onHashChange = () => {
      const id = window.location.hash.slice(1);
      if (!this.hasPanel(id)) return;
      this.select(id, false);
      this.reveal(id);
    };
    window.addEventListener('hashchange', this.onHashChange);

    // Arrivee avec #achat-<handle> : le navigateur fait lui-meme defiler vers
    // l'ancre une fois la page chargee. On ouvre l'onglet tout de suite, et on
    // recale sur le haut de la section apres son defilement a lui.
    const initial = window.location.hash.slice(1);
    if (this.hasPanel(initial)) {
      this.select(initial, false);
      const recaler = () => requestAnimationFrame(() => this.reveal(initial, true));
      if (document.readyState === 'complete') recaler();
      else window.addEventListener('load', recaler, { once: true });
    }
  }

  disconnectedCallback() {
    if (this.onLinkClick) document.removeEventListener('click', this.onLinkClick);
    if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
  }

  hasPanel(id) {
    return Boolean(id) && this.tabs.some((tab) => tab.dataset.buyTab === id);
  }

  select(id, focus) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.buyTab === id;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      if (active && focus) tab.focus();
      const panel = document.getElementById(tab.dataset.buyTab);
      if (panel) panel.hidden = !active;
    });
  }

  reveal(id, instant) {
    const section = this.closest('.buy') || this;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    section.scrollIntoView({ behavior: instant || reduced ? 'auto' : 'smooth' });
    try {
      history.replaceState(null, '', '#' + id);
    } catch (error) {
      /* l'URL reste inchangee, l'onglet est ouvert quand meme */
    }
  }

  // Fleches gauche et droite, Debut et Fin : le modele d'onglets du WAI-ARIA.
  onKeydown(event) {
    const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = null;
    if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    this.select(this.tabs[next].dataset.buyTab, true);
  }
}

if (!customElements.get('buy-pieces')) {
  customElements.define('buy-pieces', BuyPieces);
}
